import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';

/**
 * Copy-to-clipboard for a link the owner has to get onto ANOTHER device.
 *
 * The Kitchen and Cashier pages live on a tablet by the pass and a screen at the
 * till — not on the laptop the owner is reading this on. Before this, the URL was
 * plain text, so getting it across meant retyping `.../cashier/hyper-max-jordan`
 * by hand onto a touch keyboard. A single wrong character is a 404 that looks
 * like the feature is broken.
 *
 * `navigator.clipboard` needs a secure context. app.popuppush.com and the dev
 * host are both HTTPS so it is there in practice, but a venue tablet reaching a
 * LAN address over plain http would silently have no clipboard at all — hence
 * the execCommand fallback rather than a button that does nothing.
 */
export default function CopyLinkButton({ value, label }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  // The "Copied" flash outlives the click, so a click on the LAST render must
  // not fire setState after unmount (navigating away mid-flash is normal here).
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const flash = useCallback(() => {
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1600);
  }, []);

  const copy = useCallback(async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        flash();
        return;
      }
    } catch {
      // Fall through — a rejected clipboard permission is not a dead end.
    }
    // Insecure-context fallback: a throwaway textarea + the legacy copy command.
    try {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand && document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) flash();
    } catch {
      // Nothing left to try; the URL is still on screen and selectable.
    }
  }, [value, flash]);

  return (
    /* PrimeReact 8 has no `text`/`size` props (those landed in v9) — it forwards
       unknown props straight to the DOM, so `text` would leak as an invalid
       attribute and style nothing. The classes are what this version reads, and
       what honey-ink.css already targets. */
    <Button
      type="button"
      className="lty-copy-btn p-button-text p-button-sm"
      icon={copied ? 'pi pi-check' : 'pi pi-copy'}
      label={copied ? t('loyalty.copied') : t('loyalty.copy')}
      aria-label={label ? `${t('loyalty.copy')} — ${label}` : t('loyalty.copy')}
      onClick={copy}
    />
  );
}
